import { Fragment } from "react";
import ProposalCustomerApprovalActions from "@/components/sales/ProposalCustomerApprovalActions";
import ProposalRetailTermsCollapsible from "@/components/sales/ProposalRetailTermsCollapsible";
import { ProposalRetailItemsFooter } from "@/components/proposal/ProposalCommercialSummaryCard";
import ProposalCustomerPricingPanel from "@/components/proposal/ProposalCustomerPricingPanel";
import ProposalIntroSection from "@/components/proposal/ProposalIntroSection";
import ProposalPdfDownloadButton from "@/components/proposal/ProposalPdfDownloadButton";
import {
  proposalPdfDownloadLabel,
  shouldSkipProposalCustomerEngagementTracking,
} from "@shared/proposalCustomerEngagement";
import ProposalPremiumShell, {
  type ProposalPremiumCoverInput,
} from "@/components/proposal/ProposalPremiumShell";
import ProposalProductLineCard from "@/components/proposal/ProposalProductLineCard";
import { shouldEnableProposalProductImageLightbox } from "@/lib/proposalProductImageLightbox";
import {
  buildProposalSupportLinks,
  parseProposalLineSnapshot,
} from "@/lib/proposalLineSnapshot";
import { formatTransparentCustomerDetailLines } from "@shared/transparentCatalogue/customerFacingDetails";
import { explainRetailCatalogueLineTooltip } from "@/lib/proposalLineLaymanExplain";
import { resolveProposalLineAskCleoLabel } from "@shared/proposalCleoInProposalExplain";
import { PROPOSAL_COPY_FONT, proposalMoney } from "@/lib/proposalTheme";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import type { ReactNode } from "react";
import {
  JoinedRackElevation,
  type EquipmentSlot,
} from "@/pages/HrsRackConfiguratorPage";
import {
  checkHrsEquipmentFit,
  findHrsBase,
  HRS_SXR_SOLID_BRACE_CLEARANCE_IN,
} from "@shared/hrsRackConfigurator";
import type { HrsRackConfigurationData } from "@shared/hrsRackConfigurationSnapshot";
import { downloadHrsRackConfigurationPdf } from "@/lib/hrsRackConfigurationPdf";

export type RetailPremiumProposalLine = {
  id: number;
  descriptionSnapshot?: string | null;
  quantity?: number | string | null;
  lineTotal?: number | string | null;
  category?: string | null;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  rawText?: string | null;
  resolvedImageUrl?: string | null;
  imageUrlSnapshot?: string | null;
  cleoDescription?: string | null;
  customerExplanation?: string | null;
  configurationSnapshotJson?: {
    schemaVersion?: string;
    customerDisplayName?: string;
    customerDescription?: string;
    resolvedCatalogueModel?: string;
    internalOrderDescription?: string;
    completeInternalSkuRecipe?: string;
    requiresEquipmentMatching?: boolean | null;
    sourceComponent?: string | null;
    loadComponent?: string | null;
    salespersonNotes?: string | null;
    savedConfigurationId?: number;
    configurationName?: string;
    configuration?: HrsRackConfigurationData;
  } | null;
  hrsRackDetails?: {
    savedConfigurationId: number;
    configurationName: string;
    configuration: HrsRackConfigurationData;
    configuredAt: string;
    configuredPriceSgd?: number;
  } | null;
};

export type RetailPremiumApprovalActions = {
  proposalId: number;
  proposalRef: string;
  customerName?: string | null;
  customerEmail?: string | null;
  customerContact?: string | null;
  customerPricing: ProposalCustomerPricingDisplay;
  proposalStatus: string;
  approvedAt?: string | Date | null;
  approvalDisabled?: boolean;
  isSandbox?: boolean;
  salesperson?: {
    name?: string | null;
    email?: string | null;
    contact?: string | null;
  } | null;
  /** Secure Link B credential — required for public (non-staff) approval / download actions. */
  linkBCredential?: {
    readableRef: string;
    sequence: number;
    token: string;
  } | null;
};

type Props = {
  cover: ProposalPremiumCoverInput;
  proposalTitle: string;
  items: RetailPremiumProposalLine[];
  customerIntroParagraph?: string | null;
  paymentTerms?: Array<{ stage: string; percentage: number }>;
  createdAt?: string | Date | null;
  linkBExpiresAt?: string | Date | null;
  notes?: string | null;
  tradeInItemsText?: string | null;
  finalAgreedPrice: number;
  originalPrice: number;
  showFinalAgreedPrice: boolean;
  gstAmount?: number;
  showGst?: boolean;
  finalAmountPayable?: number;
  showFinalAmountPayable?: boolean;
  audience?: "customer" | "staff";
  contentLead?: ReactNode;
  approvalActions?: RetailPremiumApprovalActions;
  /** Catalogue retail: rule-based hover tooltips on line cards. */
  enableRetailLineHover?: boolean;
  /** Staff Link A: visible `?` opens Ask Cleo with a prefilled explain question. */
  onAskCleoAboutLine?: (input: { itemId: number; label: string; question: string }) => void;
  /** When false, hide per-line prices (proposal footer total still shown). */
  pricesVisibleToCustomer?: boolean;
  fullViewportCover?: boolean;
};

const money = proposalMoney;

export default function RetailPremiumProposalContent({
  cover,
  items,
  customerIntroParagraph,
  paymentTerms = [],
  createdAt,
  linkBExpiresAt,
  notes,
  tradeInItemsText,
  finalAgreedPrice,
  originalPrice,
  showFinalAgreedPrice,
  gstAmount = 0,
  showGst = false,
  finalAmountPayable = finalAgreedPrice,
  showFinalAmountPayable = showFinalAgreedPrice || showGst,
  audience = "customer",
  contentLead,
  approvalActions,
  enableRetailLineHover = false,
  onAskCleoAboutLine,
  pricesVisibleToCustomer = true,
  fullViewportCover = true,
}: Props) {
  const hideLinePrices = !pricesVisibleToCustomer;
  const showPricingBreakdown = showFinalAgreedPrice || showGst;
  const displayTotal =
    showFinalAmountPayable && showGst ? finalAmountPayable : finalAgreedPrice;

  const priceBreakdown = (
    <ProposalCustomerPricingPanel
      proposalTotal={originalPrice}
      finalAgreedPrice={finalAgreedPrice}
      showFinalAgreedPrice={showFinalAgreedPrice}
      discountAmount={approvalActions?.customerPricing.discountAmount}
      discountPercentage={approvalActions?.customerPricing.discountPercentage}
      showDiscount={approvalActions?.customerPricing.showDiscount}
      showDiscountPercentage={approvalActions?.customerPricing.showDiscountPercentage}
      gstAmount={gstAmount}
      showGst={showGst}
      finalAmountPayable={finalAmountPayable}
      showFinalAmountPayable={showFinalAmountPayable}
    />
  );

  return (
    <ProposalPremiumShell
      cover={cover}
      fullViewportCover={fullViewportCover}
      contentLead={contentLead}
    >
      <div
        className="space-y-14"
        style={{ fontFamily: PROPOSAL_COPY_FONT }}
        data-testid="retail-premium-proposal"
      >
        {customerIntroParagraph?.trim() ? (
          <ProposalIntroSection paragraph={customerIntroParagraph.trim()} />
        ) : null}

        <section className="space-y-4" data-testid="proposal-retail-items">
          {items.map(item => {
            const line = parseProposalLineSnapshot(
              String(item.descriptionSnapshot ?? "")
            );
            const canvasTitle =
              item.configurationSnapshotJson?.schemaVersion ===
              "canvas_soundbar_v1"
                ? String(
                    item.configurationSnapshotJson.customerDisplayName ?? ""
                  ).trim()
                : "";
            const title = canvasTitle || line.title;
            const detailLines = formatTransparentCustomerDetailLines({
              requiresEquipmentMatching:
                item.configurationSnapshotJson?.requiresEquipmentMatching,
              sourceComponent: item.configurationSnapshotJson?.sourceComponent,
              loadComponent: item.configurationSnapshotJson?.loadComponent,
              salespersonNotes:
                item.configurationSnapshotJson?.salespersonNotes,
            });
            const alreadyPresent = detailLines.every(detail =>
              `${line.title}\n${line.brandModel}\n${line.narrative}`.includes(
                detail.split("\n")[0] ?? detail
              )
            );
            const description =
              [line.narrative, !alreadyPresent ? detailLines.join("\n") : ""]
                .filter(Boolean)
                .join("\n\n") || null;
            const metaBits = [
              line.meta.sku ? `SKU: ${line.meta.sku}` : "",
              line.meta.uom ? `UOM: ${line.meta.uom}` : "",
              line.meta.finish ? `Finish: ${line.meta.finish}` : "",
            ]
              .filter(Boolean)
              .join(" · ");
            const rawForTooltip = [line.narrative, item.rawText]
              .filter(Boolean)
              .join("\n\n")
              .trim();
            const retailTooltip = enableRetailLineHover
              ? explainRetailCatalogueLineTooltip({
                  category: item.category,
                  name: item.name,
                  brand: item.brand,
                  model: item.model,
                  title: title || line.title,
                  brandModel: line.brandModel,
                  rawText: rawForTooltip,
                })
              : null;
            const askCleoLabel = resolveProposalLineAskCleoLabel({
              brand: item.brand,
              model: item.model,
              name: item.name,
              displayName: canvasTitle || null,
              title: title || line.title,
            });
            const showRetailLineHover = Boolean(
              enableRetailLineHover && retailTooltip && !onAskCleoAboutLine
            );

            const card = (
              <ProposalProductLineCard
                variant="retail"
                imageUrl={
                  String(
                    item.resolvedImageUrl ?? item.imageUrlSnapshot ?? ""
                  ).trim() || null
                }
                imageAlt={title || line.brandModel || "Product image"}
                title={title}
                subtitle={
                  audience === "staff" &&
                  item.configurationSnapshotJson?.schemaVersion ===
                    "canvas_soundbar_v1"
                    ? item.configurationSnapshotJson.resolvedCatalogueModel ||
                      line.brandModel ||
                      null
                    : line.brandModel || null
                }
                description={description}
                metaLine={
                  audience === "staff" &&
                  item.configurationSnapshotJson?.internalOrderDescription
                    ? item.configurationSnapshotJson.internalOrderDescription
                    : metaBits || null
                }
                quantity={item.quantity ?? 1}
                priceFormatted={money.format(Number(item.lineTotal ?? 0))}
                hidePrice={hideLinePrices}
                supportLinks={buildProposalSupportLinks(line, item)}
                cleoDescription={item.cleoDescription}
                customerExplanation={item.customerExplanation}
                productBrand={item.brand}
                productModel={item.model}
                productDisplayName={canvasTitle || item.name || null}
                interactiveHover={showRetailLineHover}
                enableImageLightbox={shouldEnableProposalProductImageLightbox(
                  audience
                )}
                onAskCleo={
                  onAskCleoAboutLine
                    ? (question) =>
                        onAskCleoAboutLine({
                          itemId: item.id,
                          label: askCleoLabel,
                          question,
                        })
                    : undefined
                }
              />
            );
            const hrsDetails =
              item.configurationSnapshotJson?.schemaVersion === "hrs_rack_v1"
                ? item.configurationSnapshotJson
                : item.hrsRackDetails;
            const hrsConfiguration = hrsDetails?.configuration;
            const hrsBase = hrsConfiguration
              ? findHrsBase(
                  hrsConfiguration.baseSeries,
                  hrsConfiguration.sizeCode
                )
              : null;
            const hrsBaseSkuFallback = hrsConfiguration
              ? hrsBase?.sku ??
                `${hrsConfiguration.series}-${hrsConfiguration.sizeCode} plinth`
              : "";
            const hrsBaseSkusBySlot = hrsConfiguration
              ? Object.fromEntries(
                  hrsConfiguration.slots.map(slot => [
                    slot.key,
                    findHrsBase(
                      slot.baseSeries ?? hrsConfiguration.baseSeries,
                      hrsConfiguration.sizeCode
                    )?.sku ?? hrsBaseSkuFallback,
                  ])
                )
              : undefined;
            const rackPresentation = hrsConfiguration ? (
              <div className="mt-3 rounded-xl border border-[#1A2744]/10 bg-[#FAFAF7] p-4">
                <div className="mx-auto max-w-3xl">
                  <JoinedRackElevation
                    series={hrsConfiguration.series}
                    finish={hrsConfiguration.finish}
                    rackLevels={hrsConfiguration.rackLevels}
                    slots={hrsConfiguration.slots.map(
                      slot =>
                        ({
                          ...slot,
                          source: "manual",
                          confidence: null,
                        }) as EquipmentSlot
                    )}
                    baseSku={hrsBaseSkuFallback}
                    baseSkusBySlot={hrsBaseSkusBySlot}
                    baseWidth={
                      hrsBase?.widthIn ??
                      Number(hrsConfiguration.sizeCode.slice(2))
                    }
                    baseDepth={
                      hrsBase?.depthIn ??
                      Number(hrsConfiguration.sizeCode.slice(0, 2))
                    }
                    onSelectSlot={() => undefined}
                    selectedSlotKey=""
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm">
                  <span>
                    {hrsDetails?.configurationName} ·{" "}
                    {hrsConfiguration.slots.filter(slot => slot.name).length}{" "}
                    equipment item(s) specified
                  </span>
                  <button
                    type="button"
                    className="font-semibold text-[#1A2744] underline"
                    onClick={() => {
                      const totalLevels = hrsConfiguration.rackLevels.reduce(
                        (sum, levels) => sum + levels,
                        0
                      );
                      const configurationCode =
                        hrsConfiguration.rackLevels.length === 1
                          ? `${hrsConfiguration.sizeCode}-${hrsConfiguration.rackLevels[0]}V`
                          : `${hrsConfiguration.sizeCode}-${hrsConfiguration.rackLevels.join("T")}`;
                      void downloadHrsRackConfigurationPdf({
                        series: hrsConfiguration.series,
                        finish: hrsConfiguration.finish,
                        configurationCode,
                        rackLevels: hrsConfiguration.rackLevels,
                        shelfSku:
                          hrsBase?.sku ??
                          `${hrsConfiguration.series}-${hrsConfiguration.sizeCode} plinth`,
                        shelfWidthIn:
                          hrsBase?.widthIn ??
                          Number(hrsConfiguration.sizeCode.slice(2)),
                        shelfDepthIn:
                          hrsBase?.depthIn ??
                          Number(hrsConfiguration.sizeCode.slice(0, 2)),
                        shelfQuantity: totalLevels,
                        solidBracePairCount: hrsConfiguration.slots.filter(
                          slot => slot.solidBrace
                        ).length,
                        configuredPrice:
                          hrsDetails &&
                          "configuredPriceSgd" in hrsDetails &&
                          hrsDetails.configuredPriceSgd
                            ? money.format(hrsDetails.configuredPriceSgd)
                            : null,
                        slots: hrsConfiguration.slots.map(slot => {
                          const [rack, level] = slot.key.split("-").map(Number);
                          const openTop =
                            hrsConfiguration.rackLevels[rack - 1] === level;
                          const braceAbove =
                            hrsConfiguration.series === "SXR" &&
                            hrsConfiguration.slots.find(
                              candidate =>
                                candidate.key === `${rack}-${level + 1}`
                            )?.solidBrace;
                          const usableHeightIn = openTop
                            ? null
                            : Math.max(
                                0,
                                slot.spacingIn -
                                  (braceAbove
                                    ? HRS_SXR_SOLID_BRACE_CLEARANCE_IN
                                    : 0)
                              );
                          return {
                            rack,
                            level,
                            openTop,
                            ...slot,
                            usableHeightIn,
                            fit: checkHrsEquipmentFit({
                              equipmentName: slot.name,
                              dimensions: slot,
                              base: hrsBase ?? null,
                              clearHeightIn: openTop
                                ? Number.POSITIVE_INFINITY
                                : (usableHeightIn ?? slot.spacingIn),
                              ventilationAllowanceIn: openTop ? 0 : 2,
                            }),
                          };
                        }),
                      });
                    }}
                  >
                    Download rack details
                  </button>
                </div>
              </div>
            ) : null;

            if (showRetailLineHover && retailTooltip) {
              return (
                <Fragment key={item.id}>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <div className="block w-full cursor-help">{card}</div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-[min(90vw,24rem)] space-y-1.5 p-3 text-left text-xs font-normal leading-snug text-balance"
                    >
                      <p className="font-medium">{retailTooltip.whatItIs}</p>
                      <p className="opacity-95">{retailTooltip.whatItDoes}</p>
                      <p className="opacity-85">{retailTooltip.whyItIsUsed}</p>
                    </TooltipContent>
                  </Tooltip>
                  {rackPresentation}
                </Fragment>
              );
            }
            return (
              <Fragment key={item.id}>
                {card}
                {rackPresentation}
              </Fragment>
            );
          })}

          <ProposalRetailItemsFooter
            totalLabel={showFinalAgreedPrice ? "FINAL PRICE" : "TOTAL"}
            totalFormatted={money.format(displayTotal)}
            breakdown={showPricingBreakdown ? priceBreakdown : null}
            notes={notes}
          />
        </section>

        {tradeInItemsText?.trim() ? (
          <section
            className="rounded-xl border border-[#C8A96A]/45 bg-[#C8A96A]/8 p-6"
            data-testid="proposal-customer-trade-in"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-[#1A2744]/70">
              Customer trade-in items
            </p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-[#1A2744]">
              {tradeInItemsText.trim()}
            </p>
          </section>
        ) : null}

        {audience !== "staff" && approvalActions ? (
          <section
            className="space-y-5 rounded-xl border border-[#1A2744]/15 bg-white/50 p-8"
            data-testid="proposal-retail-approval"
          >
            <ProposalCustomerApprovalActions
              proposalId={approvalActions.proposalId}
              proposalRef={approvalActions.proposalRef}
              status={approvalActions.proposalStatus}
              approvedAt={approvalActions.approvedAt}
              customerName={approvalActions.customerName}
              customerEmail={approvalActions.customerEmail}
              customerContact={approvalActions.customerContact}
              customerPricing={approvalActions.customerPricing}
              approvalDisabled={approvalActions.approvalDisabled}
              salesperson={approvalActions.salesperson}
              linkBCredential={approvalActions.linkBCredential}
            />
            <ProposalPdfDownloadButton
              proposalId={approvalActions.proposalId}
              customerEmail={approvalActions.customerEmail}
              linkBCredential={approvalActions.linkBCredential}
              label={proposalPdfDownloadLabel({
                audience,
                approvalDisabled: approvalActions.approvalDisabled,
                isSandbox: approvalActions.isSandbox,
              })}
              className="min-h-11 border-[#1A2744]/20 text-[#1A2744] proposal-no-print"
              variant="outline"
              skipAnalytics={shouldSkipProposalCustomerEngagementTracking({
                audience,
                approvalDisabled: approvalActions.approvalDisabled,
                isSandbox: approvalActions.isSandbox,
              })}
            />
          </section>
        ) : null}

        {audience === "staff" && approvalActions?.proposalId ? (
          <div
            className="proposal-no-print flex justify-center border-t border-[#1A2744]/10 pt-10"
            data-testid="proposal-customer-pdf-footer"
          >
            <ProposalPdfDownloadButton
              proposalId={approvalActions.proposalId}
              customerEmail={approvalActions.customerEmail}
              label={proposalPdfDownloadLabel({
                audience: "staff",
                approvalDisabled: approvalActions.approvalDisabled,
                isSandbox: approvalActions.isSandbox,
              })}
              className="min-h-11 border-[#1A2744]/20 text-[#1A2744]"
              variant="outline"
              skipAnalytics
            />
          </div>
        ) : null}

        <ProposalRetailTermsCollapsible
          paymentTerms={paymentTerms}
          createdAt={createdAt}
          linkBExpiresAt={linkBExpiresAt}
        />
      </div>
    </ProposalPremiumShell>
  );
}
